import { useState, useEffect } from 'react'
import { AppShell } from '@/components/layout/AppShell'
import { Dashboard } from '@/pages/Dashboard'
import { ModulePage } from '@/pages/ModulePage'
import { QuickReference } from '@/pages/QuickReference'
import { AdminPage } from '@/pages/AdminPage'
import { AdminLogin } from '@/components/admin/AdminLogin'
import { useAdminAuth } from '@/hooks/useAdminAuth'
import { useContentOverrides } from '@/hooks/useContentOverrides'
import { useProgress } from '@/hooks/useProgress'
import type { PageView } from '@/types'

export default function App() {
  const [view, setView] = useState<PageView>({ page: 'dashboard' })
  // Always default to light mode
  const [darkMode, setDarkMode] = useState(false)
  const [showDarkCallout, setShowDarkCallout] = useState(true)

  const auth = useAdminAuth()
  const content = useContentOverrides()
  // Single progress instance lifted here — prevents stale state between components
  const progress = useProgress()

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode)
    try { localStorage.setItem('partner-training-dark', String(darkMode)) } catch { /* ignore */ }
  }, [darkMode])

  // Dismiss the dark-mode callout after 5 s
  useEffect(() => {
    const t = setTimeout(() => setShowDarkCallout(false), 5000)
    return () => clearTimeout(t)
  }, [])

  function handleNavigate(next: PageView) {
    setView(next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <AppShell
      modules={content.mergedModules}
      currentView={view}
      onNavigate={handleNavigate}
      darkMode={darkMode}
      onToggleDark={() => setDarkMode(d => !d)}
      hasContentOverrides={Object.keys(content.overrides.modules).length > 0}
      showDarkCallout={showDarkCallout}
      onDismissDarkCallout={() => setShowDarkCallout(false)}
      progress={progress}
    >
      {view.page === 'dashboard' && (
        <Dashboard modules={content.mergedModules} progress={progress} darkMode={darkMode} onNavigate={handleNavigate} />
      )}
      {view.page === 'module' && (
        <ModulePage
          key={view.moduleId}
          moduleId={view.moduleId}
          modules={content.mergedModules}
          progress={progress}
          onNavigate={handleNavigate}
          darkMode={darkMode}
        />
      )}
      {view.page === 'quick-reference' && (
        <QuickReference modules={content.mergedModules} progress={progress} onNavigate={handleNavigate} />
      )}
      {view.page === 'admin' && (
        auth.isAuthenticated
          ? <AdminPage auth={auth} content={content} onNavigate={handleNavigate} />
          : <AdminLogin auth={auth} onCancel={() => handleNavigate({ page: 'dashboard' })} />
      )}
    </AppShell>
  )
}
